import { useCallback, useEffect, useRef, useState } from "react";
import * as augmentApi from "../../api/augment";
import * as ml from "../../api/ml";
import { listFolders, listImages } from "../../api/images";
import { getProject } from "../../api/projects";
import type { FolderList, Project } from "../../api/types";
import { CREDITS_CHANGED } from "../../lib/credits";
import { STORED_PRESETS } from "../../lib/augment";
import { augmentAdvice } from "../../lib/augmentAdvice";
import { picturesWithObjects } from "../../lib/backgroundAdvice";
import { JOB_POLL_MS, canStart, estimateLines, isActive, jobLine } from "../../lib/augmentImages";
import { backgroundEstimateLines, backgroundRule, canStartBackground } from "../../lib/backgroundImages";
import { PRO_TIP } from "../../lib/edition";
import { summarizeResults } from "../../lib/results";
import { navigate } from "../../router";
import { projectDataChanged } from "../../state/currentProject";
import { showInGallery, takeSelection } from "../../state/handoff";
import { useSession } from "../../state/session";
import { useDialogs } from "../../ui/dialogs";
import { Icon } from "../../ui/icons";
import { AugmentPreview, AugmentSettings } from "../train/AugmentPanel";

type Source = "all" | "class" | "folder" | "selected";

/**
 * Augment (Pro): make and keep augmented copies of the project's reviewed images, in a folder of their own, with their
 * annotations moved and already reviewed. A step in the flow between annotating and training: the page says when it
 * helps (few images, a rare class, a class the last run scored Needs work) and can copy just the images with one class.
 * Also "Background pictures": copies of the reviewed pictures with the objects erased, saved as Empty, for projects whose
 * users cannot photograph the empty station (`background`: opened from the Train page's advice, on that section).
 * Web only for now (docs/DESKTOP_PORT_BACKLOG.md).
 */
export function AugmentScreen({ projectId, classId, background }: { projectId: number; classId?: number; background?: boolean }) {
  const dlg = useDialogs();
  const { has, canEdit } = useSession();
  const open = has("augment_images");
  const [project, setProject] = useState<Project | null>(null);
  const [stats, setStats] = useState<ml.AnnotationStats | null>(null);
  const [weak, setWeak] = useState<string[]>([]);
  const [folders, setFolders] = useState<FolderList | null>(null);
  // Images selected on the dashboard, handed over once when it opened this page.
  const [selectedIds] = useState<number[]>(() => takeSelection(projectId));
  const [source, setSource] = useState<Source>(selectedIds.length ? "selected" : classId ? "class" : "all");
  const [cls, setCls] = useState<number | null>(classId ?? null);
  const [folder, setFolder] = useState<string>("");
  const [classIds, setClassIds] = useState<number[] | null>(null);
  const [copies, setCopies] = useState(3);
  const [recipe, setRecipe] = useState<ml.AugmentConfig>({ ...STORED_PRESETS.default });
  const [outFolder, setOutFolder] = useState("");
  const [est, setEst] = useState<augmentApi.AugmentEstimate | null>(null);
  const [jobs, setJobs] = useState<augmentApi.AugmentJob[]>([]);
  const [current, setCurrent] = useState<augmentApi.AugmentJob | null>(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // Background pictures: how many (null until the server's suggestion arrives), what that makes, and one erased picture.
  const [bgCount, setBgCount] = useState<number | null>(null);
  const [bgEst, setBgEst] = useState<augmentApi.BackgroundEstimate | null>(null);
  const [bgPreview, setBgPreview] = useState<augmentApi.BackgroundPreview | null>(null);
  const [bgPreviewing, setBgPreviewing] = useState(false);
  const [bgStarting, setBgStarting] = useState(false);
  const [bgError, setBgError] = useState("");
  const bgSection = useRef<HTMLElement | null>(null);

  const refreshStats = useCallback(() => ml.annotationStats(projectId).then(setStats).catch(() => {}), [projectId]);
  useEffect(() => {
    getProject(projectId).then(setProject).catch(() => {});
    void refreshStats();
    listFolders(projectId).then(setFolders).catch(() => {});
    ml.trainingMetrics(projectId).then((m) => setWeak(summarizeResults(m)?.classes.filter((c) => c.band === "weak").map((c) => c.name) ?? [])).catch(() => {});
  }, [projectId, refreshStats]);

  const counts = (stats?.reviewed_by_class ?? []).filter((c) => c.original_images > 0);
  // Pick the first class with images when the class source is chosen without one (or the route's class has none).
  useEffect(() => {
    if (source === "class" && counts.length && !counts.some((c) => c.class_id === cls)) setCls(counts[0].class_id);
  }, [source, counts, cls]);

  // The original images holding the chosen class, as ids: the server copies by id.
  useEffect(() => {
    setClassIds(null);
    if (source !== "class" || cls === null) return;
    let alive = true;
    listImages(projectId, { class_id: cls, augmented: "exclude" })
      .then((imgs) => alive && setClassIds(imgs.map((i) => i.id))).catch((e) => alive && setError((e as Error).message));
    return () => { alive = false; };
  }, [projectId, source, cls]);

  // What to copy, or null while it is still being worked out. An empty id list would mean "all" to the server, so a
  // class with no images is "nothing", never sent.
  const sources = useCallback((): augmentApi.AugmentSources | "nothing" | null => {
    if (source === "selected") return { image_ids: selectedIds };
    if (source === "folder") return { folder };
    if (source === "class") return classIds === null ? null : classIds.length ? { image_ids: classIds } : "nothing";
    return {};
  }, [source, selectedIds, folder, classIds]);

  // What the run would make and cost, kept current as the choices change.
  useEffect(() => {
    const s = sources();
    setEst(null);
    if (s === null) return;
    if (s === "nothing") { setEst({ source_images: 0, copies, total: 0, bytes_estimate: 0, storage_used: 0, storage_allowance: 0, storage_fits: true, metered: false }); return; }
    let alive = true;
    const t = window.setTimeout(() => {
      augmentApi.estimate(projectId, copies, s).then((e) => alive && setEst(e)).catch((e) => alive && setError((e as Error).message));
    }, 250);
    return () => { alive = false; window.clearTimeout(t); };
  }, [projectId, copies, sources]);

  // What a background run would make and cost, kept current as the number changes; the first answer fills the number in.
  useEffect(() => {
    let alive = true;
    const t = window.setTimeout(() => {
      augmentApi.backgroundEstimate(projectId, bgCount, {}).then((e) => {
        if (!alive) return;
        setBgEst(e);
        setBgCount((c) => (c === null ? e.suggested : c));
      }).catch((e) => alive && setBgError((e as Error).message));
    }, 250);
    return () => { alive = false; window.clearTimeout(t); };
  }, [projectId, bgCount]);

  const previewBackground = useCallback(async (imageId: number | null) => {
    setBgPreviewing(true); setBgError("");
    try { setBgPreview(await augmentApi.backgroundPreview(projectId, imageId)); }
    catch (e) { setBgError((e as Error).message); }
    finally { setBgPreviewing(false); }
  }, [projectId]);

  // Opened from the Train page's advice: scroll to the section and show one erased picture before anything is made.
  useEffect(() => {
    if (!background || !open) return;
    bgSection.current?.scrollIntoView({ block: "start" });
    void previewBackground(null);
  }, [background, open, previewBackground]);

  const loadJobs = useCallback(async () => {
    const list = await augmentApi.listJobs(projectId);
    setJobs(list);
    setCurrent((c) => (c ? list.find((j) => j.id === c.id) ?? c : list.find(isActive) ?? null));
    return list;
  }, [projectId]);

  // Poll while a run is going; when it ends, refresh the credits in the toolbar and the counts in the nav.
  useEffect(() => {
    void loadJobs().catch(() => {});
    return () => window.clearInterval(timer.current);
  }, [loadJobs]);
  useEffect(() => {
    window.clearInterval(timer.current);
    if (!isActive(current)) return;
    timer.current = window.setInterval(() => {
      loadJobs().then((list) => {
        const j = list.find((x) => x.id === current!.id);
        if (j && !isActive(j)) {
          window.clearInterval(timer.current);
          window.dispatchEvent(new Event(CREDITS_CHANGED));
          projectDataChanged(); void refreshStats();
        }
      }).catch(() => {});
    }, JOB_POLL_MS);
    return () => window.clearInterval(timer.current);
  }, [current?.id, current?.status, loadJobs, refreshStats]);   // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    const s = sources();
    if (!s || s === "nothing") return;
    setStarting(true); setError("");
    try {
      const job = await augmentApi.start(projectId, { ...s, augmentation: recipe, copies, output_folder: outFolder.trim() || undefined });
      setCurrent(job);
      window.dispatchEvent(new Event(CREDITS_CHANGED));
      await loadJobs();
    } catch (e) { setError((e as Error).message); }
    finally { setStarting(false); }
  };

  const generateBackgrounds = async () => {
    if (bgCount === null) return;
    setBgStarting(true); setBgError("");
    try {
      const job = await augmentApi.startBackground(projectId, { count: bgCount });
      setCurrent(job);
      window.dispatchEvent(new Event(CREDITS_CHANGED));
      await loadJobs();
    } catch (e) { setBgError((e as Error).message); }
    finally { setBgStarting(false); }
  };

  const show = (f: string) => { showInGallery(projectId, { folder: f }); navigate({ name: "dashboard" }); };

  const remove = async (j: augmentApi.AugmentJob) => {
    const ok = await dlg.confirm({ title: "Delete augmented batch", danger: true, yes: "Delete",
      text: `Delete the ${j.done} image${j.done === 1 ? "" : "s"} in "${j.folder}" and their annotations? The originals are not touched. Credits already spent are not refunded.` });
    if (!ok) return;
    try {
      await augmentApi.deleteJob(projectId, j.id);
      if (current?.id === j.id) setCurrent(null);
      await loadJobs(); projectDataChanged(); void refreshStats();
    } catch (e) { setError((e as Error).message); }
  };

  const advice = stats ? augmentAdvice(stats, weak) : null;
  const running = isActive(current);
  const past = jobs.filter((j) => !isActive(j));
  const folderNames = (folders?.folders ?? []).map((f) => f.folder).filter((f) => !f.startsWith("augmented/") && !f.startsWith("background/"));
  const pick = (s: Source) => { setError(""); setSource(s); };

  return (
    <div className="train-page" data-testid="augment-page">
      <div className="page-head">
        <div>
          <h1 className="page-title" data-testid="augment-title">Augment — {project?.name ?? ""}
            {!open && <span className="pill pro" style={{ marginLeft: 10, verticalAlign: "middle" }}>Pro</span>}</h1>
          <div className="muted">
            Make more training images from the ones you have: flipped, turned, re-lit, smaller, blurred or noisy copies, with their
            annotations moved to match and already reviewed. Most useful when there are few images, or few of one class.
          </div>
        </div>
      </div>

      {!open && (
        <div className="callout" data-testid="augment-locked"><Icon name="info" size={16} />
          <span>Augmented copies are part of Model Bench Pro. Training still varies your images on the fly (Train ▸ Advanced), so you can train without them. {PRO_TIP}</span>
        </div>
      )}
      {open && advice && (
        <div className="callout accent" data-testid="augment-advice"><Icon name="sparkles" size={16} />
          <span>{advice.text}</span>
          {advice.classId && !(source === "class" && cls === advice.classId) && (
            <button className="btn flat" onClick={() => { pick("class"); setCls(advice.classId!); }} data-testid="augment-advice-use">Use the {advice.className} images</button>
          )}
        </div>
      )}

      <div className="train-grid">
        <div className="train-main">
          <section className="card-section" data-testid="aug-source">
            <div className="section-head"><h2>1 · Which images</h2></div>
            <div className="source-list">
              <label className="check"><input type="radio" name="aug-scope" checked={source === "all"} onChange={() => pick("all")} data-testid="aug-scope-all" />
                All reviewed images{stats ? ` (${picturesWithObjects(stats)})` : ""}</label>
              <label className="check" title={counts.length ? undefined : "No class has reviewed annotations yet."}>
                <input type="radio" name="aug-scope" checked={source === "class"} disabled={!counts.length} onChange={() => pick("class")} data-testid="aug-scope-class" />
                Images with the class
                <select className="select" value={cls ?? ""} disabled={!counts.length} style={{ width: "auto", marginLeft: 8 }} data-testid="aug-class"
                  onChange={(e) => { pick("class"); setCls(Number(e.target.value)); }}>
                  {counts.map((c) => <option key={c.class_id} value={c.class_id}>{c.name} — {c.original_images} image{c.original_images === 1 ? "" : "s"}, {c.annotations} annotation{c.annotations === 1 ? "" : "s"}</option>)}
                </select></label>
              {folderNames.length > 0 && <label className="check">
                <input type="radio" name="aug-scope" checked={source === "folder"} onChange={() => { pick("folder"); if (!folder && folderNames[0]) setFolder(folderNames[0]); }} data-testid="aug-scope-folder" />
                Reviewed images in the folder
                <select className="select" value={folder} style={{ width: "auto", marginLeft: 8 }} data-testid="aug-folder-pick"
                  onChange={(e) => { pick("folder"); setFolder(e.target.value); }}>
                  {folderNames.map((f) => <option key={f} value={f}>{f}</option>)}
                </select></label>}
              {selectedIds.length > 0 && (
                <label className="check"><input type="radio" name="aug-scope" checked={source === "selected"} onChange={() => pick("selected")} data-testid="aug-scope-selected" />
                  The {selectedIds.length} image{selectedIds.length === 1 ? "" : "s"} selected on the Images page (those with reviewed annotations)</label>
              )}
            </div>
            <div className="muted">Augmented copies are never copied again.</div>
          </section>

          <section className="card-section" data-testid="aug-how">
            <div className="section-head"><h2>2 · How</h2></div>
            <div className="row" style={{ flexWrap: "nowrap", marginBottom: 8 }}>
              <span style={{ whiteSpace: "nowrap" }}>Copies per image:</span>
              <input className="input" type="number" min={1} value={copies} style={{ width: 80 }} data-testid="aug-copies"
                onChange={(e) => setCopies(Math.max(1, Math.trunc(Number(e.target.value)) || 1))} />
            </div>
            <AugmentSettings value={recipe} onChange={setRecipe} />
            <AugmentPreview projectId={projectId} value={recipe} imgSize={320} canPreview={(est?.source_images ?? 0) > 0}
              note={<>A preview. Generate makes and saves copies like these, each at its original's full resolution.</>} />
            <div className="row" style={{ flexWrap: "nowrap", marginTop: 8 }}>
              <span style={{ whiteSpace: "nowrap" }}>Save in folder:</span>
              <input className="input" placeholder="augmented/<date> x<copies> (automatic)" value={outFolder} onChange={(e) => setOutFolder(e.target.value)} data-testid="aug-folder" />
            </div>
          </section>

          <section className="card-section" data-testid="aug-background" ref={bgSection}>
            <div className="section-head"><h2>Background pictures</h2></div>
            <div className="muted" style={{ marginBottom: 8 }}>
              A model trained only on pictures that contain its objects learns that something is always there, and fires on the desk or on a
              hand. Pictures marked Empty teach it what "nothing here" looks like. Take photos of the empty station if you can: real photos are
              better. Otherwise, make them here: copies of your reviewed pictures with the objects erased, and crops of their free parts, saved
              as Empty.
            </div>
            {bgEst && <div data-testid="bg-rule" style={{ marginBottom: 8 }}>{backgroundRule(bgEst)}</div>}
            <div className="row" style={{ flexWrap: "nowrap", marginBottom: 8 }}>
              <span style={{ whiteSpace: "nowrap" }}>Background pictures to make:</span>
              <input className="input" type="number" min={0} value={bgCount ?? ""} style={{ width: 80 }} data-testid="bg-count"
                onChange={(e) => setBgCount(Math.max(0, Math.trunc(Number(e.target.value)) || 0))} />
              <button className="btn" disabled={!bgEst || bgEst.candidates === 0 || bgPreviewing} onClick={() => previewBackground(bgPreview?.image_id ?? null)} data-testid="bg-preview"
                title="See one of your reviewed pictures with its objects erased, as Generate would save it.">
                {bgPreviewing ? "Rendering…" : <><Icon name="eye" />Preview</>}</button>
              {bgPreview && <button className="btn" disabled={bgPreviewing} onClick={() => previewBackground(null)} data-testid="bg-another"><Icon name="refresh" />Another picture</button>}
            </div>
            {bgPreview && (
              <div className="augment-grid" data-testid="bg-preview-grid">
                <figure><img src={bgPreview.original} alt={`${bgPreview.filename}, as it is`} /><figcaption>Original — {bgPreview.filename}</figcaption></figure>
                <figure><img src={bgPreview.erased} alt="The same picture with its objects erased" /><figcaption>Objects erased</figcaption></figure>
                {bgPreview.crop && <figure><img src={bgPreview.crop} alt="A crop of the free part of the picture" /><figcaption>Crop of the free part</figcaption></figure>}
              </div>
            )}
            <div className="run-summary" data-testid="bg-estimate" style={{ marginTop: 8 }}>
              {bgEst ? backgroundEstimateLines(bgEst).map((l) => <div key={l}>{l}</div>) : <div>Working out the cost…</div>}
            </div>
            {bgError && <div className="error-text" data-testid="bg-error">{bgError}</div>}
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn primary" disabled={!open || !canEdit || running || bgStarting || !canStartBackground(bgEst)} onClick={generateBackgrounds} data-testid="bg-generate"
                title={!open ? PRO_TIP : !canEdit ? "Your role can look at projects but not change them" : undefined}>
                {bgStarting ? "Starting…" : <><Icon name="sparkles" />{`Make ${bgEst?.total ?? ""} background picture${bgEst?.total === 1 ? "" : "s"}`}</>}</button>
              <span className="muted" style={{ fontSize: 12 }}>Saved as Empty, in a folder of their own; they train on the training side only and are never used to score the model.</span>
            </div>
          </section>

          <section className="card-section" data-testid="aug-history-section">
            <div className="section-head"><h2>Augmented batches</h2></div>
            {past.length === 0 ? <div className="muted">None yet. Each run makes a folder of its own, which you can show or delete here.</div> : (
              <table className="augment-table" data-testid="aug-history">
                <tbody>
                  {past.map((j) => (
                    <tr key={j.id}>
                      <td>{j.folder}</td>
                      <td className="muted">{j.done} {j.kind === "background" ? "background picture" : "image"}{j.done === 1 ? "" : "s"} · {j.status}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        <button className="btn flat" disabled={!j.done} onClick={() => show(j.folder)} data-testid={`aug-show-${j.id}`}>Show</button>
                        <button className="btn flat" style={{ color: "var(--danger-text)" }} disabled={!canEdit} onClick={() => remove(j)} data-testid={`aug-delete-${j.id}`}>Delete batch</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <aside className="train-run">
          <div className="run-card" data-testid="aug-run">
            <div className="tile-label">3 · Generate</div>
            <div className="run-summary" data-testid="aug-estimate">
              {est ? estimateLines(est).map((l) => <div key={l}>{l}</div>) : <div>Working out the cost…</div>}
            </div>
            {error && <div className="error-text" data-testid="aug-error">{error}</div>}
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <button className="btn primary run-go" disabled={!open || !canEdit || running || starting || !canStart(est)} onClick={generate} data-testid="aug-generate"
                title={!open ? PRO_TIP : !canEdit ? "Your role can look at projects but not change them" : undefined}>
                {starting ? "Starting…" : <><Icon name="sparkles" />{`Generate ${est?.total ?? ""} image${est?.total === 1 ? "" : "s"}`}</>}</button>
              {running && current && <button className="btn danger" disabled={current.cancel_requested} onClick={() => augmentApi.cancelJob(projectId, current.id).then(loadJobs).catch((e) => setError((e as Error).message))} data-testid="aug-cancel"><Icon name="stop" />Cancel</button>}
            </div>
            {current && (
              <div data-testid="aug-job">
                <div className="progress"><i style={{ width: `${Math.round((current.done / Math.max(current.total, 1)) * 100)}%` }} /><span>{current.done} / {current.total}</span></div>
                <div className={current.status === "failed" ? "error-text" : "muted"} data-testid="aug-job-line">{jobLine(current)}</div>
                {!isActive(current) && current.done > 0 && (
                  <div className="row" style={{ marginTop: 6 }}>
                    <button className="btn" onClick={() => show(current.folder)} data-testid="aug-show"><Icon name="folder" />Show these images</button>
                    <button className="btn flat" onClick={() => navigate({ name: "train", projectId })} data-testid="aug-go-train">Train with them →</button>
                  </div>
                )}
              </div>
            )}
            <div className="muted" style={{ fontSize: 12 }}>
              Copies train on the training side only, next to their original, and are never used to score the model.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
